import React, { useState, useEffect } from 'react';
import { CoverArt, getStatus } from '../utils/books';

const STATUS_OPTIONS = [
  { key: 'reading',   label: 'Reading now' },
  { key: 'finished',  label: 'Finished' },
  { key: 'wishlist',  label: 'Want to read' },
  { key: 'abandoned', label: 'Abandoned' },
];

function StarRating({ value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(n => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(value === n ? 0 : n)}
          onMouseEnter={() => setHover(n)}
          onMouseLeave={() => setHover(0)}
          className={`text-2xl leading-none transition-colors ${
            n <= (hover || value) ? 'text-gold' : 'text-ink-muted hover:text-gold/40'
          }`}
        >
          ★
        </button>
      ))}
    </div>
  );
}

function BookDetailDrawer({ book, onClose, onSave, onDelete }) {
  const [status,     setStatus]     = useState(getStatus(book));
  const [progress,   setProgress]   = useState(book.progress || 0);
  const [startDate,  setStartDate]  = useState(
    book.startDate  ? new Date(book.startDate).toISOString().split('T')[0]  : ''
  );
  const [finishDate, setFinishDate] = useState(
    book.finishDate ? new Date(book.finishDate).toISOString().split('T')[0] : ''
  );
  const [rating,  setRating]  = useState(book.rating || 0);
  const [note,    setNote]    = useState(book.note || '');
  const [saving,  setSaving]  = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  const close = () => {
    setVisible(false);
    setTimeout(onClose, 250);
  };

  const save = async () => {
    setSaving(true);
    await onSave({
      status,
      progress,
      startDate:  startDate  || null,
      finishDate: finishDate || null,
      rating:     rating     || null,
      note,
    });
    setSaving(false);
    close();
  };

  const confirmDelete = () => {
    if (window.confirm(`Remove "${book.title}" from your library?`)) {
      onDelete(book);
      close();
    }
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex justify-end transition-colors duration-250 ${
        visible ? 'bg-black/50 backdrop-blur-sm' : 'bg-transparent'
      }`}
      onClick={close}
    >
      <div
        className={`w-full max-w-md bg-ink-surface border-l border-ink-border h-full overflow-y-auto flex flex-col shadow-2xl transition-transform duration-250 ${
          visible ? 'translate-x-0' : 'translate-x-full'
        }`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-4 px-6 pt-6 pb-5 border-b border-ink-border flex-shrink-0">
          <div className="w-12 h-18 rounded-lg overflow-hidden flex-shrink-0" style={{ height: '4.5rem' }}>
            <CoverArt book={book} className="w-full h-full" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-cream font-semibold text-sm leading-snug line-clamp-2">{book.title}</p>
            <p className="text-cream-muted text-xs mt-0.5">{book.author}</p>
          </div>
          <button
            onClick={close}
            className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full text-cream-muted hover:text-cream hover:bg-ink-raised transition-colors text-lg"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 px-6 py-6 space-y-6 overflow-y-auto">

          {/* Status */}
          <div>
            <label className="text-cream-muted text-xs font-medium block mb-2">Status</label>
            <div className="grid grid-cols-2 gap-2">
              {STATUS_OPTIONS.map(o => (
                <button
                  key={o.key}
                  type="button"
                  onClick={() => setStatus(o.key)}
                  className={`text-xs px-3 py-2 rounded-lg border transition-colors ${
                    status === o.key
                      ? 'bg-gold text-ink-base border-gold font-semibold'
                      : 'border-ink-border text-cream-dim hover:border-ink-muted hover:text-cream'
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          {/* Progress */}
          {(status === 'reading' || status === 'abandoned') && (
            <div>
              <label className="text-cream-muted text-xs font-medium block mb-2">
                Progress — <span className="text-cream">{progress}%</span>
              </label>
              <input
                type="range" min="0" max="100" value={progress}
                onChange={e => setProgress(+e.target.value)}
                className="w-full accent-gold"
              />
            </div>
          )}

          {/* Star rating */}
          {status === 'finished' && (
            <div>
              <label className="text-cream-muted text-xs font-medium block mb-2">Rating</label>
              <StarRating value={rating} onChange={setRating} />
              {rating > 0 && (
                <button
                  type="button"
                  onClick={() => setRating(0)}
                  className="text-[11px] text-cream-muted hover:text-cream mt-1.5 transition-colors"
                >
                  Clear rating
                </button>
              )}
            </div>
          )}

          {/* Dates */}
          {status !== 'wishlist' && (
            <div className="space-y-3">
              {(status === 'reading' || status === 'finished' || status === 'abandoned') && (
                <div>
                  <label className="text-cream-muted text-xs font-medium block mb-1.5">Date started</label>
                  <input
                    type="date" value={startDate}
                    onChange={e => setStartDate(e.target.value)}
                    className="sc-input text-xs py-1.5"
                  />
                </div>
              )}
              {status === 'finished' && (
                <div>
                  <label className="text-cream-muted text-xs font-medium block mb-1.5">Date finished</label>
                  <input
                    type="date" value={finishDate}
                    onChange={e => setFinishDate(e.target.value)}
                    className="sc-input text-xs py-1.5"
                  />
                </div>
              )}
            </div>
          )}

          {/* Note */}
          <div>
            <label className="text-cream-muted text-xs font-medium block mb-2">Note</label>
            <textarea
              className="sc-input min-h-[140px] resize-none text-sm"
              placeholder="Your thoughts, quotes, what stayed with you…"
              value={note}
              onChange={e => setNote(e.target.value)}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-ink-border flex items-center gap-2 flex-shrink-0">
          <button
            onClick={save}
            disabled={saving}
            className="sc-btn-gold text-sm py-2 px-6 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button onClick={close} className="sc-btn-ghost text-sm py-2 px-4">
            Cancel
          </button>
          <button
            onClick={confirmDelete}
            className="ml-auto text-xs text-crimson/60 hover:text-crimson transition-colors"
          >
            Remove book
          </button>
        </div>
      </div>
    </div>
  );
}

export default BookDetailDrawer;
